import { makeStyles } from '@material-ui/styles'
import ApiClient from 'api-client/ApiClient'
import { dnsaasDoc } from 'app/docLinks'
import { useAppSelector } from 'app/store'
import CodeBlock from 'core/components/CodeBlock'
import ExternalLink from 'core/components/ExternalLink'
import TitleWithInfoIcon from 'core/components/TitleWithInfoIcon'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import {
  default as DropdownField,
  default as PicklistField,
} from 'core/components/validatedForm/DropdownField'
import Info from 'core/components/validatedForm/Info'
import MultiDropdownField from 'core/components/validatedForm/MultiDropdownField'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import Theme from 'core/themes/model'
import { listImages } from 'openstack/components/images/actions'
import { imagesSelector } from 'openstack/components/images/selectors'
import { keys } from 'ramda'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { trackEvent } from 'utils/tracking'
import { listAllVolumes } from '../../storage/volumes/actions'
import { allVolumesSelector } from '../../storage/volumes/selectors'
import {
  assignDns,
  assignHostConfig,
  assignHypervisorRole,
  assignImageLibraryRole,
  assignPersistentStorageRole,
  deauthorizeHypervisorRole,
  deauthorizeImageLibraryRole,
  deauthorizePersistentStorageRole,
  getBlueprints,
  getResmgrRoleConfig,
  getVmsForHost,
  listResmgrHosts,
  removeDns,
  removeRemoteSupportRole,
  unassignHostConfig,
  updateRemoteSupportRole,
} from '../actions'
import ClusterHostGroupsPicklist from '../clusters/ClusterHostGroupsPicklist'
import {
  hasDnsRole,
  hasHypervisorRole,
  hasImageLibraryRole,
  hasRemoteSupportRole,
} from '../helpers'
import { resmgrHostsSelector } from '../selectors'
import HostNetworkConfigPicklist from './HostNetworkConfigPicklist'
import { useVolumeBackends } from '../../storage/volume-types/VolumeBackendsProvider'
import EllipsisText from 'k8s/components/common/EllipsisText'

const defaultParams = { valuesByHost: {} }

const ErrorText = ({ children }) => {
  const classes = useStyles({})
  return (
    <Text variant="body2" className={classes.errorText}>
      {children}
    </Text>
  )
}

// Component for hypervisor/cluster cell with proper styling
const HypervisorClusterCell = ({
  host,
  params,
  updateParams,
  vmsByHost,
  originalHostRoles,
  isGPUSupportedHost,
  classes: parentClasses,
  submitAttempted,
  setSubmitAttempted,
}) => {
  const disabled =
    !params.valuesByHost?.[host.id]?.hypervisor ||
    !!originalHostRoles?.valuesByHost?.[host.id]?.hostcluster
  const error =
    submitAttempted &&
    params.valuesByHost?.[host.id]?.hypervisor &&
    !params.valuesByHost?.[host.id]?.hostcluster

  const cellClasses = useStyles({ disabled, error })

  return (
    <td className={`${parentClasses.bodyCell} ${parentClasses.colHypervisorCluster}`}>
      <div className={cellClasses.hypervisorClusterContainer}>
        <div className={cellClasses.checkboxWrapper}>
          <CheckboxField
            id={`valuesByHost.${host.id}.hypervisor`}
            label=""
            value={params.valuesByHost?.[host.id]?.hypervisor}
            readOnly={vmsByHost?.[host.id]?.length > 0}
            info={
              vmsByHost?.[host.id]?.length > 0
                ? 'Cannot remove hypervisor role while VMs are present on this host'
                : ''
            }
            onChange={(value) => {
              if (!value && error) {
                setSubmitAttempted(false)
              }
              updateParams({
                valuesByHost: {
                  ...params.valuesByHost,
                  [host.id]: {
                    ...params.valuesByHost[host.id],
                    hypervisor: value,
                    // Clear cluster when hypervisor is unchecked
                    // hostcluster: value ? params.valuesByHost[host.id].hostcluster : null,
                  },
                },
              })
            }}
          />
        </div>
        <div className={cellClasses.clusterDropdownWrapper}>
          <DropdownField
            id={`${host.id}-hostcluster`}
            DropdownComponent={ClusterHostGroupsPicklist}
            value={params.valuesByHost?.[host.id]?.hostcluster}
            onChange={(value) => {
              updateParams({
                valuesByHost: {
                  ...params.valuesByHost,
                  [host.id]: {
                    ...params.valuesByHost[host.id],
                    hostcluster: value,
                  },
                },
              })
            }}
            placeholder="Select Cluster"
            disabled={disabled}
            tooltip={
              originalHostRoles?.valuesByHost?.[host.id]?.hostcluster
                ? 'You can only change the cluster when the host is drained and deauthorized'
                : !params.valuesByHost?.[host.id]?.hypervisor
                ? 'You must enable the hypervisor role first'
                : ''
            }
            filterFn={(cluster) => {
              // Not non-gpu supported host - show only non-gpu enabled clusters
              if (!isGPUSupportedHost) {
                return !cluster?.gpu?.enabled
              }

              // GPU supported host - show only clusters with compatible gpu mode
              if (cluster?.gpu?.enabled) {
                return host?.compatibleModes?.includes(cluster?.gpu?.mode)
              }

              return false
            }}
          />
        </div>
      </div>
      {error && <ErrorText>Cluster is required</ErrorText>}
    </td>
  )
}

const { nova, neutron } = ApiClient.getInstance()

export default function EditServerRolesModal({ rows: hosts, onClose, allHostsDetails }) {
  const classes = useStyles({})
  const [submitting, setSubmitting] = useState(false)
  const [originalHostRoles, setOriginalHostRoles] = useState({ valuesByHost: {} })
  const [blueprintSettings, setBlueprintSettings] = useState({
    storageBackends: {},
    imageLibrarySharedStorage: false,
  })
  const [loaded, setLoaded] = useState(false)
  const [showRetryMessage, setShowRetryMessage] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [vmsByHost, setVmsByHost] = useState({})
  const [healthCheckFailed, setHealthCheckFailed] = useState(null)
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const rowRefs = useRef<Record<string, HTMLTableRowElement>>({})
  const initializedRef = useRef(false)
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)

  const scrollToFirstError = useCallback(() => {
    const errorHost = hosts.find((host) => {
      const hasClusterError =
        params?.valuesByHost?.[host.id]?.hypervisor && !params?.valuesByHost?.[host.id]?.hostcluster
      const hasHostConfigError = !params?.valuesByHost?.[host.id]?.hostNetworkConfig
      return hasClusterError || hasHostConfigError
    })

    if (errorHost && rowRefs.current[errorHost.id]) {
      rowRefs.current[errorHost.id].scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [hosts, params])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const makeCalls = async () => {
      try {
        const hypervisorHosts = hosts.filter((host) => host.roles.includes('hypervisor'))
        const results: any = await Promise.allSettled(
          hypervisorHosts.map((host) => getVmsForHost(host?.id)),
        )
        const values = results?.map((result) => result?.value)
        const valuesByHost = hypervisorHosts.reduce(
          (accum, host, idx) => ({
            ...accum,
            [host.id]: values[idx],
          }),
          {},
        )
        setVmsByHost(valuesByHost)
      } catch (error) {
        console.error('Error fetching VMs for hosts:', error)
      }
    }
    makeCalls()
  }, [hosts])

  const volumeBackendConfigs = useMemo(() => {
    const volumeTypes = keys(blueprintSettings.storageBackends)
    const configurations = volumeTypes.reduce((accum, type) => {
      const configNames = keys(blueprintSettings.storageBackends[type])
      const configsWithVolumeType = configNames.map((name) => {
        return {
          // @ts-ignore
          ...blueprintSettings.storageBackends[type][name],
          name: name,
          volumeType: type,
        }
      })
      return [...accum, ...configsWithVolumeType]
    }, [])
    return configurations
  }, [blueprintSettings])

  const volumeBackendConfigOptions = useMemo(() => {
    return volumeBackendConfigs.map((config) => {
      const label = `${config.name} (${config.volumeType}, ${config.driver})`
      return {
        label,
        value: config.name,
        content: (
          <EllipsisText title={label} variant="body2" showTooltip maxWidth={180}>
            {label}
          </EllipsisText>
        ),
      }
    })
  }, [volumeBackendConfigs])

  const allHosts = useSelector(resmgrHostsSelector)

  const { reload: reloadVolumes } = useListAction(listAllVolumes, {
    loadOnDemand: true,
  })

  const allVolumes = useAppSelector(allVolumesSelector)

  // Only get volumes if a persistent storage node is selected
  useEffect(() => {
    if (hosts.some((host) => host?.roles?.includes('persistent-storage'))) {
      reloadVolumes(true)
    }
  }, [hosts])

  const { reload } = useListAction(listResmgrHosts, {})

  const { loading: loadingImages } = useListAction(listImages)
  const imagesList = useAppSelector(imagesSelector)

  const enableRoleCheckbox = useCallback(
    (host, roleName, hasRoleFunc) => {
      if (params.valuesByHost?.[host.id]?.[roleName]) {
        return true
      }
      const roleHost = allHosts.find(hasRoleFunc)
      const roleChecked = hosts.some((h) => !!params.valuesByHost?.[h.id]?.[roleName])
      if (roleHost) {
        return roleHost.id === host.id
      }
      return !roleChecked
    },
    [allHosts, params, hosts],
  )

  const populateHostData = (hosts, storageHostBackends) => {
    return hosts.reduce(
      (accum, host) => ({
        valuesByHost: {
          ...accum.valuesByHost,
          [host.id]: {
            hostNetworkConfig: host?.hostconfig_id,
            hypervisor: hasHypervisorRole(host),
            imageLibrary: hasImageLibraryRole(host),
            remoteSupport: hasRemoteSupportRole(host),
            dns: hasDnsRole(host),
            volumeBackends:
              keys(storageHostBackends.find((h) => h.host.id === host.id)?.backends) || [],
            attachedVolumes: allVolumes?.filter((volume) =>
              volume['os-vol-host-attr:host']?.includes(host?.id),
            ),
            hostcluster: host?.cluster?.name,
          },
        },
      }),
      { valuesByHost: {} },
    )
  }

  const { volumeBackendsPerHost, reloadVolumeBackends } = useVolumeBackends()

  useEffect(() => {
    // Prevent running initialization multiple times
    if (initializedRef.current) {
      return
    }
    initializedRef.current = true

    const querySettings = async () => {
      try {
        const blueprints = await getBlueprints()
        const blueprint = blueprints?.[0]
        // @ts-ignore
        setBlueprintSettings(blueprint)

        const storageHosts = hosts.filter((host) => host.roles.includes('persistent-storage'))
        // const results = await Promise.allSettled(
        //   storageHosts.map((host) =>
        //     getResmgrRoleConfig({ role: 'pf9-cindervolume-config', host: host }),
        //   ),
        // )
        const storageHostBackends = storageHosts.map((host) => {
          return {
            host,
            backends: volumeBackendsPerHost.get(host.id) || [],
          }
        })
        const hostData = populateHostData(hosts, storageHostBackends)
        setOriginalHostRoles(hostData)
        setParams(hostData)
      } catch (error) {
        console.error('Error initializing form data:', error)
        // Even on error, set default host data to prevent empty form
        const defaultHostData = populateHostData(hosts, [])
        setOriginalHostRoles(defaultHostData)
        setParams(defaultHostData)
      } finally {
        setLoaded(true)
      }
    }
    querySettings()
  }, [hosts, volumeBackendsPerHost])

  const checkServicesHealth = async () => {
    setHealthCheckFailed(null)
    try {
      await nova.checkHealth()
      await neutron.checkHealth()
      return true
    } catch (e) {
      setHealthCheckFailed({
        title: 'Error',
        message: 'Hypervisor services are not ready. Please try again after sometime',
      })
      return false
    }
  }

  // 🩹 Rollback imageLibrary checkbox state if failed
  const rollbackImageLibraryState = useCallback(
    (errHostId) => {
      const originalImageLibrary = originalHostRoles?.valuesByHost?.[errHostId]?.imageLibrary

      if (typeof originalImageLibrary !== 'boolean') {
        return
      }
      // Rollback the specific host's imageLibrary value
      setParams({
        ...params,
        valuesByHost: {
          ...(params?.valuesByHost ?? {}),
          [errHostId]: {
            ...(params?.valuesByHost?.[errHostId] ?? {}),
            imageLibrary: originalImageLibrary,
          },
        },
      })
    },
    [params, originalHostRoles, setParams],
  )

  const submitForm = useCallback(async () => {
    let shouldReloadVolumeBackends = false

    // Validate required cluster selection for enabled hypervisors
    const missingClusterHost = hosts?.find(
      (host) =>
        params?.valuesByHost?.[host.id]?.hypervisor &&
        !params?.valuesByHost?.[host.id]?.hostcluster,
    )

    const missingHostConfig = hosts?.find(
      (host) => !params?.valuesByHost?.[host.id]?.hostNetworkConfig,
    )

    if (missingClusterHost || missingHostConfig) {
      setSubmitAttempted(true)
      scrollTimeoutRef.current = setTimeout(scrollToFirstError, 10)
      return
    }

    const rolesToAdd = []
    const rolesToRemove = []
    setSubmitting(true)

    // For enabling new hypervisor roles - Check health of nova and neutron services before submitting
    const isAddingHypervisorRole = hosts.some((host) => {
      return (
        originalHostRoles?.valuesByHost?.[host.id]?.hypervisor === false &&
        params?.valuesByHost?.[host.id]?.hypervisor === true
      )
    })
    if (isAddingHypervisorRole) {
      const heathCheckOk = await checkServicesHealth()
      if (!heathCheckOk) {
        setSubmitting(false)
        return
      }
    }

    for (const host of hosts) {
      // Hypervisor role - check if hypervisor checkbox changed OR cluster changed (when hypervisor is enabled)
      if (
        (originalHostRoles?.valuesByHost?.[host.id]?.hypervisor === false &&
          params?.valuesByHost?.[host.id]?.hypervisor === true) ||
        (params?.valuesByHost?.[host.id]?.hypervisor &&
          originalHostRoles?.valuesByHost?.[host.id]?.hostcluster !==
            params?.valuesByHost?.[host.id]?.hostcluster)
      ) {
        rolesToAdd.push({
          role: 'hypervisor',
          host,
          hostcluster: params?.valuesByHost?.[host.id]?.hostcluster,
        })
      }

      // Remove hypervisor if: hypervisor checkbox was unchecked
      if (
        originalHostRoles?.valuesByHost?.[host.id]?.hypervisor === true &&
        params?.valuesByHost?.[host.id]?.hypervisor === false
      ) {
        rolesToRemove.push({ role: 'hypervisor', host })
      }

      if (
        originalHostRoles?.valuesByHost?.[host.id]?.imageLibrary === false &&
        params?.valuesByHost?.[host.id]?.imageLibrary === true
      ) {
        rolesToAdd.push({ role: 'imageLibrary', host })
      }
      if (
        params?.valuesByHost?.[host.id]?.volumeBackends?.length &&
        originalHostRoles?.valuesByHost?.[host.id]?.volumeBackends?.sort() !==
          params?.valuesByHost?.[host.id]?.volumeBackends?.sort()
      ) {
        shouldReloadVolumeBackends = true
        rolesToAdd.push({
          role: 'persistentStorage',
          host,
          volumeBackends: params.valuesByHost[host.id].volumeBackends,
        })
      }
      if (
        originalHostRoles?.valuesByHost?.[host.id]?.remoteSupport === false &&
        params?.valuesByHost?.[host.id]?.remoteSupport === true
      ) {
        rolesToAdd.push({ role: 'remoteSupport', host })
      }
      if (
        originalHostRoles?.valuesByHost?.[host.id]?.dns === false &&
        params?.valuesByHost?.[host.id]?.dns === true
      ) {
        rolesToAdd.push({ role: 'dns', host })
      }
      if (
        originalHostRoles?.valuesByHost?.[host.id]?.imageLibrary === true &&
        params?.valuesByHost?.[host.id]?.imageLibrary === false
      ) {
        rolesToRemove.push({ role: 'imageLibrary', host })
      }
      if (
        originalHostRoles?.valuesByHost?.[host.id]?.volumeBackends.length &&
        !params?.valuesByHost?.[host.id]?.volumeBackends.length
      ) {
        shouldReloadVolumeBackends = true
        rolesToRemove.push({ role: 'persistentStorage', host })
      }
      if (
        originalHostRoles?.valuesByHost?.[host.id]?.remoteSupport === true &&
        params?.valuesByHost?.[host.id]?.remoteSupport === false
      ) {
        rolesToRemove.push({ role: 'remoteSupport', host })
      }
      if (
        originalHostRoles?.valuesByHost?.[host.id]?.dns === true &&
        params?.valuesByHost?.[host.id]?.dns === false
      ) {
        rolesToRemove.push({ role: 'dns', host })
      }
    }

    // Need to unassign host configs that we are changing
    const addHostConfigHosts = hosts.filter((host) => {
      return host.hostconfig_id === null
    })

    const updateHostConfigHosts = hosts.filter((host) => {
      return (
        host.hostconfig_id &&
        host.hostconfig_id !== params?.valuesByHost?.[host.id]?.hostNetworkConfig
      )
    })

    const hostConfigUnassignmentResults = await Promise.allSettled(
      updateHostConfigHosts.map((host) => {
        return unassignHostConfig({
          hostId: host.id,
          configId: host.hostconfig_id,
        })
      }),
    )

    // Do not assign if they selected the None option
    const filteredConfigHosts = updateHostConfigHosts.filter(
      (host) => params?.valuesByHost?.[host.id]?.hostNetworkConfig !== 'none',
    )
    const hostConfigAssignmentResults = await Promise.allSettled(
      [...addHostConfigHosts, ...filteredConfigHosts].map((host) => {
        return assignHostConfig({
          hostId: host.id,
          configId: params?.valuesByHost?.[host.id]?.hostNetworkConfig,
        })
      }),
    )

    if (
      // @ts-ignore
      [...hostConfigAssignmentResults, ...hostConfigUnassignmentResults].some(
        (result) => result.status === 'rejected',
      )
    ) {
      // @ts-ignore
      const errIndex = hostConfigAssignmentResults.findIndex(
        (result) => result.status === 'rejected',
      )
      const errHost = hosts[errIndex]
      // @ts-ignore
      const err = `${errHost.info.hostname} - ${hostConfigAssignmentResults[errIndex]?.reason?.err}`
      setSubmitting(false)
      setErrorMessage(err)
      setShowRetryMessage(true)
      return
    }
    const results = await Promise.allSettled(
      rolesToAdd.map((role) => {
        if (role.role === 'hypervisor') {
          return assignHypervisorRole({ host: role.host, hostcluster: role.hostcluster })
        } else if (role.role === 'imageLibrary') {
          return assignImageLibraryRole({ host: role.host })
        } else if (role.role === 'persistentStorage') {
          return assignPersistentStorageRole({
            host: role.host,
            body: {
              backends: role.volumeBackends,
            },
          })
        } else if (role.role === 'remoteSupport') {
          return updateRemoteSupportRole({ host: role.host })
        } else if (role.role === 'dns') {
          return assignDns({ host: role.host })
        } else {
          return null
        }
      }),
    )
    trackEvent('Edit Roles - Add Roles', { roles: rolesToAdd })
    if (results.some((result) => result.status === 'rejected')) {
      trackEvent('Edit Roles - Add Roles Failed', { results })
      const errIndex = results.findIndex((result) => result.status === 'rejected')
      const errHost = rolesToAdd[errIndex]?.host
      // @ts-ignore
      const err = `${errHost.info.hostname} - ${results[errIndex]?.reason?.err}`

      if (errHost?.id) {
        rollbackImageLibraryState(errHost.id)
      }
      setSubmitting(false)
      setErrorMessage(err)
      setShowRetryMessage(true)
      return
    }
    // if (rolesToAdd.some((role) => role.role === 'hypervisor')) {
    //   await waitSeconds(5)
    // }

    const removeResults = await Promise.allSettled(
      rolesToRemove.map((role) => {
        if (role.role === 'imageLibrary') {
          return deauthorizeImageLibraryRole({ host: role.host })
        } else if (role.role === 'hypervisor') {
          return deauthorizeHypervisorRole({ host: role.host })
        } else if (role.role === 'persistentStorage') {
          return deauthorizePersistentStorageRole({ host: role.host })
        } else if (role.role === 'remoteSupport') {
          return removeRemoteSupportRole({ hostId: role.host.id })
        } else if (role.role === 'dns') {
          return removeDns({ hostId: role.host.id })
        }
        return null
      }),
    )
    trackEvent('Edit Roles - Remove Roles', { roles: rolesToRemove })
    if (removeResults.some((result) => result.status === 'rejected')) {
      trackEvent('Edit Roles - Remove Roles Failed', { results: removeResults })
      const errIndex = removeResults.findIndex((result) => result.status === 'rejected')
      const errHost = rolesToRemove[errIndex]?.host
      // @ts-ignore

      let errMsg = ''
      const removeResult = removeResults[errIndex]
      if (removeResult && removeResult.status === 'rejected') {
        // @ts-ignore
        errMsg = removeResult.reason?.err?.message || removeResult.reason?.err || 'Unknown error'
        // @ts-ignore
        if (removeResult.reason?.err?.code === 'ERR_BAD_RESPONSE') {
          errMsg = 'Something went wrong. Please try again.'
        }
      }

      const err = `${errHost.info.hostname} - ${errMsg}`
      if (errHost?.id) {
        rollbackImageLibraryState(errHost.id)
      }
      setSubmitting(false)
      setErrorMessage(err)
      setShowRetryMessage(true)
      return
    }
    // Reload VolumeBackend after removing roles to ensure UI reflects the changes
    if (shouldReloadVolumeBackends) reloadVolumeBackends()
    reload(true, true)
    handleClose()
  }, [params, hosts, reloadVolumeBackends])

  const handleClose = () => {
    setParams(defaultParams)
    initializedRef.current = false
    onClose()
  }

  // Only one image library for now, so just check images list
  // const vmImageLength = (host) => {
  //   return host?.vms?.map((vm) => vm?.image).length
  // }

  const noImagesPresent = useMemo(() => {
    if (loadingImages) {
      return false
    }
    return imagesList.length === 0
  }, [imagesList, loadingImages])

  // Disable multiple image library hosts if shared storage is not enabled in blueprint
  // Only one host can have image library role at a time
  const isImageLibraryDisabled = useCallback(
    (hostId: string) => {
      const { imageLibrarySharedStorage } = blueprintSettings ?? {}
      const valuesByHost = (params?.valuesByHost ?? {}) as Record<
        string,
        { imageLibrary?: boolean }
      >

      // If shared storage is enabled, no restriction
      if (imageLibrarySharedStorage) {
        return false
      }

      // Check if any host already has the image-library role in the backend (saved state)
      const existingImageLibraryHost = allHostsDetails?.find((h) =>
        h.roles?.includes('image-library'),
      )?.id

      // Check if any host (in the form) currently has imageLibrary checked
      const selectedHostInForm = Object.entries(valuesByHost)?.find(
        ([, val]) => val?.imageLibrary,
      )?.[0]

      // If shared storage is disabled and another host (not this one) is selected in the form → disable
      if (selectedHostInForm && selectedHostInForm !== hostId) {
        return true
      }

      // If shared storage is disabled and there’s an existing image-library host,
      // only that host can be modified (unchecked). Others remain disabled.
      if (existingImageLibraryHost && existingImageLibraryHost !== hostId) {
        return true
      }

      // Allow toggling on/off for the same host (update allowed)
      return false
    },
    [allHostsDetails, blueprintSettings?.imageLibrarySharedStorage, params?.valuesByHost],
  )

  return (
    <ModalForm
      open
      title={`Edit Roles`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={submitting}
      submitTitle={`Update Role Assignment`}
      maxWidth={1360}
      loading={!loaded || submitting}
      loadingMessage={
        submitting ? 'Updating host roles, please do not close this dialog' : 'Loading'
      }
      panel="dialog"
      customErrorComponent={showRetryMessage ? <ErrorText>{errorMessage}</ErrorText> : null}
      error={healthCheckFailed ? healthCheckFailed : null}
      showConfirmationOnCancel
    >
      <Text variant="body2" className={classes.description}>
        Use this modal to assign or modify roles for selected cluster hosts. Choose configurations
        or enable features like Hypervisor, VM Image Library, Persistent Storage, and more, then
        click Update Role Assignment to save changes.
      </Text>
      <div className={classes.tableContainer}>
        <table className={classes.table}>
          <thead>
            <tr className={classes.headerRow}>
              <th className={`${classes.headerCell} ${classes.colServer}`}>
                <Text variant="caption1">Server</Text>
              </th>
              <th className={`${classes.headerCell} ${classes.colHostConfig}`}>
                <TitleWithInfoIcon
                  titleText={<Text variant="caption1">Host Config</Text>}
                  tooltipMsg="For GPU hosts, select the gpu enabled host config"
                />
              </th>
              <th className={`${classes.headerCell} ${classes.colHypervisorCluster}`}>
                <TitleWithInfoIcon
                  titleText={<Text variant="caption1">Hypervisor Cluster</Text>}
                  tooltipMsg={
                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                      <li>
                        The Hypervisor role will be enabled automatically once the cluster has been
                        assigned.
                      </li>
                      <li>Unassigning a cluster will remove the Hypervisor role.</li>
                    </ul>
                  }
                />
              </th>
              <th className={`${classes.headerCell} ${classes.colVmImage}`}>
                <Text variant="caption1"> Image Library</Text>
              </th>
              <th className={`${classes.headerCell} ${classes.colPersistentStorage}`}>
                <Text variant="caption1">Persistent Storage</Text>
              </th>
              <th className={`${classes.headerCell} ${classes.colRemoteSupport}`}>
                <Text variant="caption1">Advanced Remote Support</Text>
              </th>
              <th className={`${classes.headerCell} ${classes.colDns}`}>
                <Text variant="caption1">DNS</Text>
              </th>
            </tr>
          </thead>
          <tbody>
            {hosts.map((host) => {
              const isGPUSupportedHost = host?.gpuModels?.length > 0
              const configPicklistDisabled = host.v1Roles?.filter((role) => role !== 'pf9-support')
                ?.length
              return (
                <React.Fragment key={host.id}>
                  <tr className={classes.bodyRow} ref={(el) => (rowRefs.current[host.id] = el)}>
                    <td className={`${classes.bodyCell} ${classes.colServer}`}>
                      <Text variant="body2" className={classes.name}>
                        {host.info.hostname}
                      </Text>
                    </td>
                    <td className={`${classes.bodyCell} ${classes.colHostConfig}`}>
                      <PicklistField
                        DropdownComponent={HostNetworkConfigPicklist}
                        name="hostNetworkConfig"
                        id={`valuesByHost.${host.id}.hostNetworkConfig`}
                        key={`valuesByHost.${host.id}.hostNetworkConfig`}
                        label=""
                        compact={false}
                        value={params.valuesByHost?.[host.id]?.hostNetworkConfig}
                        tooltip={
                          configPicklistDisabled ? (
                            <Text variant="body2" className={classes.tooltip}>
                              You may only change the network configuration when no roles are
                              installed on the host.
                            </Text>
                          ) : null
                        }
                        disabled={configPicklistDisabled}
                        disabledMessage
                        onChange={(value) =>
                          updateParams({
                            valuesByHost: {
                              ...params.valuesByHost,
                              [host.id]: {
                                ...params.valuesByHost[host.id],
                                hostNetworkConfig: value,
                              },
                            },
                          })
                        }
                        required
                      />
                    </td>
                    <HypervisorClusterCell
                      host={host}
                      params={params}
                      updateParams={updateParams}
                      vmsByHost={vmsByHost}
                      originalHostRoles={originalHostRoles}
                      isGPUSupportedHost={isGPUSupportedHost}
                      classes={classes}
                      submitAttempted={submitAttempted}
                      setSubmitAttempted={setSubmitAttempted}
                    />
                    <td className={`${classes.bodyCell} ${classes.colVmImage}`}>
                      <CheckboxField
                        id={`valuesByHost.${host.id}.imageLibrary`}
                        label=""
                        value={params.valuesByHost?.[host.id]?.imageLibrary}
                        disabled={isImageLibraryDisabled(host.id)}
                        className={classes.marginAuto}
                        info={
                          isImageLibraryDisabled(host.id) ? (
                            'The image library role can be assigned to multiple hosts only if shared storage is enabled in the cluster blueprint. Remove the image library role from the other host and save the changes first.'
                          ) : !noImagesPresent && hasImageLibraryRole(host) ? (
                            <div>
                              <Text variant="body2" className={classes.tooltip}>
                                <b>WARNING</b>: If removing the image library role from this host,
                                ensure one of the following, or the images will become unusable
                                after the role is removed.
                              </Text>
                              <Text variant="body2" className={classes.tooltip}>
                                1. There will still be an existing image library host present, and
                                the hosts are using shared filesystem storage.
                              </Text>
                              <Text variant="body2" className={classes.tooltip}>
                                2. There will still be an existing image library host present, and
                                the image storage path on the blueprint was set to a volume type,
                                meaning images are stored on a persistent storage host.
                              </Text>
                              {!blueprintSettings?.imageLibrarySharedStorage && (
                                <Text variant="body2" className={classes.tooltip}>
                                  3. The image library role must be removed from this host and saved
                                  before it can be assigned to another host.
                                </Text>
                              )}
                            </div>
                          ) : (
                            ''
                          )
                        }
                        onChange={(value) =>
                          updateParams({
                            valuesByHost: {
                              ...params.valuesByHost,
                              [host.id]: {
                                ...params.valuesByHost[host.id],
                                imageLibrary: value,
                              },
                            },
                          })
                        }
                      />
                    </td>
                    <td className={`${classes.bodyCell} ${classes.colPersistentStorage}`}>
                      <MultiDropdownField
                        id={`valuesByHost.${host.id}.volumeBackends`}
                        label=""
                        options={volumeBackendConfigOptions}
                        value={params.valuesByHost?.[host.id]?.volumeBackends}
                        tooltip={
                          params.valuesByHost?.[host.id]?.attachedVolumes?.length > 0 ? (
                            <Text variant="body2" className={classes.tooltip}>
                              <b>Warning</b>: You cannot remove this role because one or more
                              volumes are still attached. Please detach all volumes before
                              proceeding.
                            </Text>
                          ) : (
                            ''
                          )
                        }
                        onChange={(value) =>
                          updateParams({
                            valuesByHost: {
                              ...params.valuesByHost,
                              [host.id]: {
                                ...params.valuesByHost[host.id],
                                volumeBackends: value,
                              },
                            },
                          })
                        }
                        fullWidth
                      />
                    </td>
                    <td className={`${classes.bodyCell} ${classes.colRemoteSupport}`}>
                      <CheckboxField
                        id={`valuesByHost.${host.id}.remoteSupport`}
                        label=""
                        className={classes.marginAuto}
                        value={params.valuesByHost?.[host.id]?.remoteSupport}
                        onChange={(value) =>
                          updateParams({
                            valuesByHost: {
                              ...params.valuesByHost,
                              [host.id]: {
                                ...params.valuesByHost[host.id],
                                remoteSupport: value,
                              },
                            },
                          })
                        }
                      />
                    </td>
                    <td className={`${classes.bodyCell} ${classes.colDns}`}>
                      <CheckboxField
                        id={`valuesByHost.${host.id}.dnsRole`}
                        label=""
                        value={params.valuesByHost?.[host.id]?.dns}
                        className={classes.marginAuto}
                        disabled={!enableRoleCheckbox(host, 'dns', hasDnsRole)}
                        info={
                          !enableRoleCheckbox(host, 'dns', hasDnsRole)
                            ? 'Only one cluster server may be a dns role. Please deauthorize your existing dns if you would like to assign a new dns.'
                            : ''
                        }
                        onChange={(value) =>
                          updateParams({
                            valuesByHost: {
                              ...params.valuesByHost,
                              [host.id]: {
                                ...params.valuesByHost[host.id],
                                dns: value,
                              },
                            },
                          })
                        }
                      />
                    </td>
                  </tr>
                  {params.valuesByHost?.[host.id]?.dns && (
                    <tr>
                      <td colSpan={7} className={classes.instructionsCell}>
                        <Info className={classes.instructions}>
                          <Text variant="body2">
                            To complete the process of making this host a Designate Node, you will
                            need to create a custom pools.yaml file.
                            <br />
                            Then log in to the host and run the command below with your custom
                            pools.yaml file:
                            <br />
                            <br />
                            <CodeBlock>
                              {'designate-manage pool update --file <path/to/pools.yaml>'}
                            </CodeBlock>
                            <br />
                            <br />
                            Refer to the following
                            <ExternalLink url={dnsaasDoc} newWindow={true}>
                              {' '}
                              article{' '}
                            </ExternalLink>
                            for more information.
                          </Text>
                        </Info>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </ModalForm>
  )
}

interface StyleProps {
  disabled?: boolean
  error?: boolean
}

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  description: {
    color: theme.components.typography.passive,
    marginBottom: 24,
    lineHeight: 1.5,
  },
  tableContainer: {
    border: `1px solid ${theme.components.table.border}`,
    borderRadius: 4,
    overflowX: 'auto',
    overflowY: 'hidden',
    marginBottom: 8,
  },
  table: {
    width: '100%',
    minWidth: 1100,
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
  },
  headerRow: {
    borderBottom: `1px solid ${theme.components.table.border}`,
    backgroundColor: theme.components.table.hoverBackground,
  },
  headerCell: {
    padding: '16px 12px',
    textAlign: 'left',
    fontWeight: 400,
    verticalAlign: 'top',
    color: theme.components.typography.passive,
    fontSize: 12,
  },
  // Column width classes - total should be <= 100%, remaining space auto-distributed
  colServer: {
    width: '18%',
  },
  colHostConfig: {
    width: '14%',
  },
  colHypervisorCluster: {
    width: '22%',
  },
  colVmImage: {
    width: '8%',
    textAlign: 'center',
  },
  colPersistentStorage: {
    width: '20%',
  },
  colRemoteSupport: {
    width: '11%',
    textAlign: 'center',
  },
  colDns: {
    width: '5%',
    textAlign: 'center',
  },
  hypervisorClusterContainer: {
    display: 'flex',
    alignItems: 'center',
    borderRadius: '4px',
    gap: 8,
    ...theme.typography.inputPlaceholder,
    marginTop: 1,
    position: 'relative',
    backgroundColor: ({ disabled }) =>
      disabled ? 'transparent' : theme.components.input.frame.background,
    color: theme.components.input.frame.color,
    borderColor: ({ error }) =>
      error ? theme.components.input.error : theme.components.input.frame.border,
    borderWidth: 1,
    borderStyle: 'solid',
    padding: theme.spacing(0, 1),
  },
  checkboxWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  clusterDropdownWrapper: {
    flex: 1,
    minWidth: 0,
    '& .inputFrame': {
      border: 'none',
      cursor: ({ disabled }) => (disabled ? 'not-allowed' : 'pointer'),
      borderLeft: `1px solid ${theme.components.table.border}`,
      borderRadius: '0',
    },
    '& .dropdown-container': {
      maxWidth: '100% !important',
    },
  },
  bodyRow: {
    borderBottom: `1px solid ${theme.components.table.border}`,
  },
  bodyCell: {
    padding: '16px 12px',
    verticalAlign: 'top',
  },
  bodyCellCentered: {
    padding: '16px 12px',
    verticalAlign: 'middle',
    textAlign: 'center',
    '& > div': {
      justifyContent: 'center',
    },
  },
  tooltip: {
    color: theme.components.tooltip.text,
  },
  tooltipIcon: {
    color: theme.components.tooltip.text,
    marginRight: 8,
  },

  name: {
    wordBreak: 'break-all',
    paddingTop: 8,
  },
  instructions: {
    margin: 0,
  },
  instructionsCell: {
    padding: '12px 16px',
    borderBottom: `1px solid ${theme.components.table.border}`,
  },
  marginAuto: {
    margin: 'auto',
    paddingTop: 8,
  },
  errorText: {
    color: theme.palette.red[500],
  },
}))
